import dayjs, { Dayjs } from 'dayjs';

import { availabilityEndDay, availabilityRangeStartDay } from '@/shared/lib/availability';

import {
  TIMELINE_PERIOD_COLUMN_WIDTH,
  TIMELINE_PERIOD_FIELD_PREFIX,
  TimelineAvailabilityWindow,
  TimelineBooking,
  TimelineGranularity,
  TimelineRange,
  UnavailableStatus,
} from './timeline.types';
import { toUnavailableStatus } from './unavailability';

const startOfPeriod = (date: Dayjs, granularity: TimelineGranularity): Dayjs =>
  granularity === 'week' ? date.startOf('isoWeek') : date.startOf('month');

const endOfPeriod = (date: Dayjs, granularity: TimelineGranularity): Dayjs =>
  granularity === 'week' ? date.endOf('isoWeek') : date.endOf('month');

const addPeriod = (date: Dayjs, granularity: TimelineGranularity): Dayjs =>
  granularity === 'week' ? date.add(1, 'week') : date.add(1, 'month');

/**
 * How wide one period column is drawn.
 *
 * A month column carries four to five times the days a week column does, so at the same width every
 * bar inside it is squeezed to a stub and a fortnight is barely a pixel apart from a month. Months
 * get twice the room; weeks are already legible at the base width.
 */
export const timelinePeriodColumnWidth = (granularity: TimelineGranularity): number =>
  granularity === 'month' ? TIMELINE_PERIOD_COLUMN_WIDTH * 2 : TIMELINE_PERIOD_COLUMN_WIDTH;

/**
 * The value a timeline bar carries for a backend START instant: the bare calendar day it means.
 *
 * Both renderers and the bookings axis resolve every bar edge to its LOCAL calendar day (the
 * `startOf('day')` snap in TimelinePeriodCell/TimelineStrip, `dayStartMs` in bookingsAxis, the
 * local format in barTooltipTitle). A raw backend instant only lands on the right local day by
 * coincidence of the viewer's zone — the UTC-canon shapes land one day off east of UTC. Collapsing
 * per the backend's own range rule at assembly makes the local read the meant day in every zone.
 */
export const timelineBarStart = (value: string): string => availabilityRangeStartDay(value);

/**
 * The value a timeline bar carries for a backend END instant: the meant day, stretched to its
 * LOCAL end of day.
 *
 * The stretch is load-bearing, not cosmetic. The day-snap renderers read the same day off a bare
 * day and off its end-of-day instant — but the gap and conflict math work in instant space, and a
 * bare-day end (local midnight) would leave the bar's own last day uncovered, letting
 * computeAvailableGaps advertise a green day that is actually booked.
 */
export const timelineBarEnd = (value: string): string => {
  const day = dayjs(availabilityEndDay(value));
  return day.isValid() ? day.endOf('day').toISOString() : value;
};

export const getPeriodFieldName = (index: number): string =>
  `${TIMELINE_PERIOD_FIELD_PREFIX}${index}`;

export const isPeriodField = (field: string): boolean =>
  field.startsWith(TIMELINE_PERIOD_FIELD_PREFIX);

export const buildTimelineRange = ({
  bookingsMin,
  bookingsMax,
  minOverride,
  maxOverride,
  granularity,
  minPeriods = 0,
}: {
  bookingsMin?: Dayjs;
  bookingsMax?: Dayjs;
  minOverride?: Dayjs;
  maxOverride?: Dayjs;
  granularity: TimelineGranularity;
  /** Ensure at least this many period columns even when the date span is short. */
  minPeriods?: number;
}): TimelineRange => {
  const now = dayjs();
  const resolvedMin = minOverride ?? bookingsMin ?? now;
  const resolvedMax = maxOverride ?? bookingsMax ?? now;

  const snappedStart = startOfPeriod(resolvedMin, granularity);
  const unit = granularity === 'week' ? 'week' : 'month';
  // Breathing room ahead of the first bar — but only when the range was inferred from the bars
  // themselves. An explicit range is exactly what the caller asked the endpoint for, so padding it
  // would draw a period nothing was fetched for, which reads as empty rather than as unknown.
  const paddedStart = minOverride ? snappedStart : snappedStart.subtract(1, unit);
  let snappedEnd = endOfPeriod(
    resolvedMax.isBefore(resolvedMin) ? resolvedMin : resolvedMax,
    granularity,
  );

  // Only a range inferred from the bars needs a floor: a handful of days would otherwise draw as
  // one or two columns. An explicit end is the window the caller asked the endpoint for, and
  // stretching it hangs empty columns off a rail that was deliberately kept short.
  if (minPeriods > 0 && !maxOverride) {
    const minimumEnd = endOfPeriod(paddedStart.add(minPeriods - 1, unit), granularity);
    if (snappedEnd.isBefore(minimumEnd)) snappedEnd = minimumEnd;
  }

  const periods: Dayjs[] = [];
  let cursor = paddedStart;
  while (cursor.isSameOrBefore(snappedEnd)) {
    periods.push(cursor);
    cursor = addPeriod(cursor, granularity);
  }
  // Period starts map to column centers, so a date late in the last period resolves to
  // an x beyond that column. This extra trailing period hosts that overflow so end caps
  // and the highlight band don't get clipped at the grid edge.
  periods.push(cursor);

  return { start: paddedStart, end: snappedEnd, periods };
};

export const collectBookingBounds = <R>(
  rows: R[],
  getRowBookings: (row: R) => TimelineBooking[],
): { min?: Dayjs; max?: Dayjs } => {
  let min: Dayjs | undefined;
  let max: Dayjs | undefined;

  for (const row of rows) {
    for (const booking of getRowBookings(row) ?? []) {
      const start = dayjs(booking.start as Date | string);
      const end = dayjs(booking.end as Date | string);
      if (!min || start.isBefore(min)) min = start;
      if (!max || end.isAfter(max)) max = end;
    }
  }

  return { min, max };
};

export const formatPeriodHeader = (period: Dayjs, granularity: TimelineGranularity): string =>
  granularity === 'week' ? period.format('DD.MM.YYYY') : period.format('MMM YYYY');

// Maps a date to a horizontal pixel offset within the timeline. Each period's start
// date lands on the center of its column (the period-start line), so a date partway
// through a period is interpolated between two center lines — giving date-accurate
// bar/highlight positions instead of whole-cell snapping.
export const offsetForDate = (
  date: Dayjs,
  range: TimelineRange,
  granularity: TimelineGranularity,
  cellWidth: number,
): number => {
  // The +0.5 shift aligns period-start dates (Monday for weeks, 1st for months) with
  // the vertical indicator line drawn at 50% of each column cell (see styles).
  if (granularity === 'week') {
    const dateWeekStart = date.startOf('isoWeek');
    const weekIndex = Math.round(dateWeekStart.diff(range.start, 'week', true));
    const actualWeekStart = range.start.add(weekIndex, 'week');
    const weekLength = actualWeekStart.add(1, 'week').diff(actualWeekStart);
    const fraction = date.diff(actualWeekStart) / weekLength;
    return (weekIndex + 0.5 + fraction) * cellWidth;
  }

  const dateMonthStart = date.startOf('month');
  const monthIndex = Math.round(dateMonthStart.diff(range.start, 'month', true));
  const actualMonthStart = range.start.add(monthIndex, 'month');
  const monthLength = actualMonthStart.add(1, 'month').diff(actualMonthStart);
  const fraction = date.diff(actualMonthStart) / monthLength;
  return (monthIndex + 0.5 + fraction) * cellWidth;
};

export const toDayjs = (value: string | Date | Dayjs | undefined): Dayjs | undefined => {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) return value;
  return dayjs(value as string | Date);
};

/**
 * The day a bookings-axis WINDOW bound means — the campaign period on the order and media-plan
 * rails, which arrives as a raw backend instant.
 *
 * The window's edges are minted as axis boundaries alongside every bar's own, and those are
 * collapsed per the backend's range rule first (timelineBarStart/timelineBarEnd). Reading the
 * window's in the browser's zone instead put its edge a day off the identical bar edge east of
 * UTC (ends) and west of it (starts), splitting one shared boundary into two: the rail then drew
 * a spurious one-day segment past the last booking and labelled the header with the booking's own
 * end date AND the day after it. Collapsing both sides the same way is what lets the two coincide
 * and dedupe.
 *
 * Only a string can carry that ambiguity; a Date or Dayjs is already a resolved instant a caller
 * picked deliberately (the pickers' own ±1 month axis window), so it passes through untouched.
 */
export const timelineWindowStart = (value?: string | Date | Dayjs): Dayjs | undefined =>
  typeof value === 'string' ? toDayjs(timelineBarStart(value)) : toDayjs(value);

/** The end-side twin of {@link timelineWindowStart} — the meant day, at its own local midnight,
 *  which is exactly where a bar ending that day puts its boundary (`dayStartMs` in bookingsAxis). */
export const timelineWindowEnd = (value?: string | Date | Dayjs): Dayjs | undefined =>
  typeof value === 'string' ? toDayjs(availabilityEndDay(value)) : toDayjs(value);

const dayOf = (value: TimelineBooking['start']): Dayjs => toDayjs(value)!.startOf('day');

/** The calendar days (`YYYY-MM-DD`) on which any of `bars` starts — feed `fuseAdjacentBars`. */
export const barStartDays = (bars: TimelineBooking[]): Set<string> =>
  new Set(bars.map((bar) => dayOf(bar.start).format('YYYY-MM-DD')));

/**
 * Flags every bar that ends the day before a bar — on ANY row of the rail — starts.
 *
 * A bar ends at the START of its end day, so its boundary can carry that day's date ("30 Nov"),
 * and the booking that begins the next morning opens at "01 Dec". Left alone, the rail slices a
 * one-day column between the two — a gap where there is none. A flagged bar is drawn
 * (TimelinePeriodCell, TimelineStrip) and measured (bookingsAxis) through its end day instead, so
 * the two dates share one boundary, printed as a stacked pair. Its `end` is untouched: the
 * tooltip still names the day the booking really ends, and the identity of an unflagged bar is
 * kept for memoised consumers.
 *
 * `startDays` is collected across the whole rail (barStartDays over every row's bars), not per
 * row: boundaries are rail-wide, so a booking ending on the 30th on one row and one starting on
 * the 1st on another would otherwise still mint both lines with the one-day column between them.
 */
export const fuseAdjacentBars = (
  bars: TimelineBooking[],
  startDays: ReadonlySet<string>,
): TimelineBooking[] =>
  bars.map((bar) =>
    startDays.has(dayOf(bar.end).add(1, 'day').format('YYYY-MM-DD'))
      ? { ...bar, abutsNext: true }
      : bar,
  );

/**
 * The bars as a row that shows nothing before `day` (a bare `YYYY-MM-DD`) draws them: a bar over
 * by then is dropped, and one still running is cut to open on it.
 *
 * Compared as instants, like the conflict math whose output this usually trims: the piece
 * splitBarsAroundConflicts leaves before a clash ends on the clash's own midnight, so a rival cut
 * where the row starts is over by then. An untouched bar keeps its identity.
 */
export const barsFromDay = (bars: TimelineBooking[], day: string): TimelineBooking[] => {
  const from = dayjs(day);
  if (!from.isValid()) return bars;

  return bars.flatMap((bar) => {
    if (!dayjs(bar.start as string | Date).isBefore(from)) return [bar];
    return dayjs(bar.end as string | Date).isAfter(from) ? [{ ...bar, start: day }] : [];
  });
};

export interface AvailableGapsOptions {
  /** The row's own availability answer for the searched period, where it reports one. */
  rowStatus?: string;
  /** The row's own availability window from the timeline endpoints, where known. */
  availabilityWindow?: TimelineAvailabilityWindow;
}

interface GapPiece {
  id: string;
  start: Dayjs;
  end: Dayjs;
}

interface SplitBounds {
  start: Dayjs;
  endExclusive: Dayjs;
}

const gapBar = (
  id: string,
  start: Dayjs,
  end: Dayjs,
  unavailableStatus?: UnavailableStatus,
): TimelineBooking => ({
  id,
  start: start.toISOString(),
  end: end.toISOString(),
  isOwn: false,
  status: 'AVAILABLE',
  unavailableStatus,
});

/**
 * The local-midnight bounds green may run between, or undefined where the blanket stamp must stay.
 *
 * Only a PARTIALLY_AVAILABLE row with a known window splits: BOOKED and NA name causes the window
 * cannot explain, and without a window there is no boundary to cut at. The last guard is the
 * safety net — a window that covers the whole period with nothing booked means the backend
 * refused for a cause this split cannot see, and a rail must never advertise green on a row the
 * table will then refuse to select.
 *
 * The bounds collapse per the backend's own day rules (availabilityRangeStartDay /
 * availabilityEndDay): the stored instants are UTC canon, and reading them in the browser's zone
 * lands a day off east of UTC.
 */
const resolveSplitBounds = (
  unavailableStatus: UnavailableStatus | undefined,
  availabilityWindow: TimelineAvailabilityWindow | undefined,
  blocking: TimelineBooking[],
  periodStart: Dayjs,
  periodEnd: Dayjs,
): SplitBounds | undefined => {
  if (unavailableStatus !== 'PARTIALLY_AVAILABLE') return undefined;
  if (!availabilityWindow?.start || !availabilityWindow?.end) return undefined;

  const startDay = availabilityRangeStartDay(availabilityWindow.start);
  const endDay = availabilityEndDay(availabilityWindow.end);

  const bounds = { start: dayjs(startDay), endExclusive: dayjs(endDay).add(1, 'day') };
  if (!bounds.start.isValid() || !bounds.endExclusive.isValid()) return undefined;

  const coversPeriod =
    !bounds.start.isAfter(periodStart) && !bounds.endExclusive.isBefore(periodEnd);
  const hasBlockingOverlap = blocking.some(
    (bar) =>
      dayjs(bar.start as string | Date).isBefore(periodEnd) &&
      dayjs(bar.end as string | Date).isAfter(periodStart),
  );
  if (coversPeriod && !hasBlockingOverlap) return undefined;

  return bounds;
};

/**
 * Cuts one free gap at the window bounds: the stretch inside stays green, the stretches outside
 * carry OUTSIDE_AVAILABILITY. A piece that is the whole gap keeps the gap's id; only a gap that
 * was actually cut mints suffixed ids, so React keys stay stable everywhere nothing changed —
 * the same rule splitBarsAroundConflicts follows.
 */
const splitGapAroundBounds = (gap: GapPiece, bounds: SplitBounds): TimelineBooking[] => {
  const greenStart = gap.start.isAfter(bounds.start) ? gap.start : bounds.start;
  const greenEnd = gap.end.isBefore(bounds.endExclusive) ? gap.end : bounds.endExclusive;

  if (!greenStart.isBefore(greenEnd)) {
    return [gapBar(gap.id, gap.start, gap.end, 'OUTSIDE_AVAILABILITY')];
  }
  if (greenStart.isSame(gap.start) && greenEnd.isSame(gap.end)) {
    return [gapBar(gap.id, gap.start, gap.end)];
  }

  const pieces: TimelineBooking[] = [];
  if (gap.start.isBefore(greenStart)) {
    pieces.push(gapBar(`${gap.id}-pre`, gap.start, greenStart, 'OUTSIDE_AVAILABILITY'));
  }
  pieces.push(gapBar(`${gap.id}-in`, greenStart, greenEnd));
  if (greenEnd.isBefore(gap.end)) {
    pieces.push(gapBar(`${gap.id}-post`, greenEnd, gap.end, 'OUTSIDE_AVAILABILITY'));
  }
  return pieces;
};

// Fills the free space between the booked/blocked bars inside [windowStart, windowEnd]
// with synthetic AVAILABLE bars, so a row reads as a continuous line of availability
// interrupted by bookings rather than a sparse set of disconnected bars.
//
// `rowStatus` is the row's own answer about the window, which occupancy cannot give. Where it says
// PARTIALLY_AVAILABLE and the row's availability window is known, the free stretches split at the
// window's edges — green inside, "outside availability" beyond — so the viewer sees which part of
// the period fails and why. Everywhere else an unavailable answer stamps the gaps whole, exactly
// as before: the stretch is still unsold, so it stays AVAILABLE, but it is no longer offered.
export const computeAvailableGaps = (
  bars: TimelineBooking[],
  windowStart: Dayjs,
  windowEnd: Dayjs,
  idPrefix: string,
  { rowStatus, availabilityWindow }: AvailableGapsOptions = {},
): TimelineBooking[] => {
  const unavailableStatus = toUnavailableStatus(rowStatus);
  const sorted = bars
    .filter((b) => b.status !== 'AVAILABLE')
    .sort(
      (a, b) =>
        dayjs(a.start as string | Date).valueOf() - dayjs(b.start as string | Date).valueOf(),
    );

  const gaps: GapPiece[] = [];
  let cursor = windowStart;
  let idx = 0;

  for (const bar of sorted) {
    const barStart = dayjs(bar.start as string | Date);
    const barEnd = dayjs(bar.end as string | Date);
    if (barEnd.isBefore(windowStart) || barStart.isAfter(windowEnd)) continue;
    const effectiveStart = barStart.isBefore(windowStart) ? windowStart : barStart;
    if (cursor.isBefore(effectiveStart)) {
      gaps.push({ id: `avail-${idPrefix}-gap${idx++}`, start: cursor, end: effectiveStart });
    }
    const effectiveEnd = barEnd.isAfter(windowEnd) ? windowEnd : barEnd;
    if (effectiveEnd.isAfter(cursor)) cursor = effectiveEnd;
  }

  if (cursor.isBefore(windowEnd)) {
    gaps.push({ id: `avail-${idPrefix}-tail`, start: cursor, end: windowEnd });
  }

  const splitBounds = resolveSplitBounds(
    unavailableStatus,
    availabilityWindow,
    sorted,
    windowStart,
    windowEnd,
  );
  if (!splitBounds) {
    return gaps.map((gap) => gapBar(gap.id, gap.start, gap.end, unavailableStatus));
  }

  return gaps.flatMap((gap) => splitGapAroundBounds(gap, splitBounds));
};

const earlier = (a: Dayjs, b: Dayjs): Dayjs => (a.isBefore(b) ? a : b);
const later = (a: Dayjs, b: Dayjs): Dayjs => (a.isAfter(b) ? a : b);

const isOutsideAvailability = (bar: TimelineBooking): boolean =>
  bar.unavailableStatus === 'OUTSIDE_AVAILABILITY';

/** Joins "Outside availability" bars that meet end to start into one, under the first one's id. */
const joinOutsideAvailability = (bars: TimelineBooking[]): TimelineBooking[] => {
  const joined = bars
    .filter(isOutsideAvailability)
    .sort(
      (a, b) =>
        dayjs(a.start as string | Date).valueOf() - dayjs(b.start as string | Date).valueOf(),
    )
    .reduce<TimelineBooking[]>((acc, bar) => {
      const last = acc[acc.length - 1];
      if (last && dayjs(last.end as string | Date).isSame(dayjs(bar.start as string | Date))) {
        acc[acc.length - 1] = { ...last, end: bar.end };
      } else {
        acc.push(bar);
      }
      return acc;
    }, []);

  return [...bars.filter((bar) => !isOutsideAvailability(bar)), ...joined];
};

/**
 * A picked period's free stretches (`gaps`, from computeAvailableGaps) plus "Outside availability"
 * over the rest of `rail`, wherever the row's own availability window does not reach — for a rail
 * that shows a margin around the period, as the add-booking dialogs do. Inside the period the gap
 * split already says so; a margin piece meeting one of those at a period edge is joined to it, so
 * the stretch reads as one segment under one label.
 *
 * Free margin stays empty, since green answers only the period, and what `blocking` occupies is
 * left to its own bar. An unknown window says nothing; one known to be empty (an inverted
 * intersection) covers the whole margin.
 */
export const withOutsideAvailabilityMargins = (
  gaps: TimelineBooking[],
  blocking: TimelineBooking[],
  period: { start: Dayjs; end: Dayjs },
  rail: { start: Dayjs; end: Dayjs },
  availabilityWindow: TimelineAvailabilityWindow | undefined,
  idPrefix: string,
): TimelineBooking[] => {
  if (!availabilityWindow?.start || !availabilityWindow?.end) return gaps;

  const opens = dayjs(availabilityRangeStartDay(availabilityWindow.start));
  const closes = dayjs(availabilityEndDay(availabilityWindow.end)).add(1, 'day');
  if (!opens.isValid() || !closes.isValid()) return gaps;
  const neverOpen = !opens.isBefore(closes);

  const margins = [
    { id: `${idPrefix}-before`, start: rail.start, end: earlier(period.start, rail.end) },
    { id: `${idPrefix}-after`, start: later(period.end, rail.start), end: rail.end },
  ];

  const outside = margins.flatMap((margin) =>
    (neverOpen
      ? [margin]
      : [
          { id: `${margin.id}-early`, start: margin.start, end: earlier(margin.end, opens) },
          { id: `${margin.id}-late`, start: later(margin.start, closes), end: margin.end },
        ]
    )
      .filter((piece) => piece.start.isBefore(piece.end))
      .flatMap((piece) =>
        computeAvailableGaps(blocking, piece.start, piece.end, piece.id).map(
          (bar): TimelineBooking => ({ ...bar, unavailableStatus: 'OUTSIDE_AVAILABILITY' }),
        ),
      ),
  );

  return outside.length ? joinOutsideAvailability([...gaps, ...outside]) : gaps;
};

/**
 * Where a period someone is about to book runs into occupancy that already blocks it.
 *
 * Only ever fed APPROVED occupancy by callers: a pending booking — always the viewer's own, since
 * the endpoint withholds everybody else's — reserves nothing and must not be drawn as a clash.
 */
export const computeConflictBars = (
  blocking: TimelineBooking[],
  from: Dayjs,
  to: Dayjs,
  rowId: string,
): TimelineBooking[] =>
  blocking.flatMap((bar, index) => {
    const barStart = dayjs(bar.start as string | Date);
    const barEnd = dayjs(bar.end as string | Date);
    const overlapStart = from.isAfter(barStart) ? from : barStart;
    const overlapEnd = to.isBefore(barEnd) ? to : barEnd;

    if (!overlapStart.isBefore(overlapEnd)) return [];

    return [
      {
        id: `conflict-${rowId}-${index}`,
        start: overlapStart.toISOString(),
        end: overlapEnd.toISOString(),
        isOwn: false,
        status: 'CONFLICT' as const,
      },
    ];
  });
